import { cva } from "class-variance-authority";
import {
	AlertCircle,
	AlertTriangle,
	Ban,
	CheckCircle,
	Clock,
	Loader2,
	type LucideIcon,
} from "lucide-react";
import type { ComponentProps } from "react";

import { cn } from "@/lib/utils";

import {
	type StatusTypeVisibleToUser,
	displayStatus,
	statusDescriptions,
	statusToStatusesVisibleToUser,
} from "@intents-explorer/shared-api/status";

import { Tooltip, TooltipContent, TooltipTrigger } from "@defuse/ui/tooltip";

const statusVariants = cva(
	"inline-flex items-center justify-center rounded-full font-medium",
	{
		variants: {
			variant: {
				PENDING_DEPOSIT:
					"bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-400",
				PROCESSING:
					"bg-blue-100 text-blue-800 dark:bg-blue-500/20 dark:text-blue-400",
				SUCCESS:
					"bg-green-100 text-green-800 dark:bg-green-500/20 dark:text-green-400",
				REFUNDED:
					"bg-purple-100 text-purple-800 dark:bg-purple-500/20 dark:text-purple-400",
				FAILED: "bg-red-100 text-red-800 dark:bg-red-500/20 dark:text-red-400",
				INCOMPLETE_DEPOSIT:
					"bg-orange-100 text-orange-800 dark:bg-orange-500/20 dark:text-orange-400",
			} satisfies Record<StatusTypeVisibleToUser, string>,
			size: {
				default: "gap-1.5 px-2.5 py-1.5 text-xs",
				lg: "gap-2 px-3 py-2 text-sm",
			},
		},
	},
);

export const statusIcons = {
	PENDING_DEPOSIT: Clock,
	PROCESSING: Loader2,
	SUCCESS: CheckCircle,
	REFUNDED: Ban,
	FAILED: AlertCircle,
	INCOMPLETE_DEPOSIT: AlertTriangle,
} satisfies Record<StatusTypeVisibleToUser, LucideIcon>;

interface StatusProps extends ComponentProps<"div"> {
	status: StatusTypeVisibleToUser;
	size?: "default" | "lg" | undefined;
}

export function Status({
	status,
	size = "default",
	className,
	...props
}: StatusProps & {
	collapsible?: boolean;
}) {
	const variant = statusToStatusesVisibleToUser[status];
	const displayText = displayStatus(status);

	const description =
		status in statusDescriptions ? statusDescriptions[status] : undefined;

	const Icon = statusIcons[variant];

	const content = (
		<div
			className={cn(
				statusVariants({ variant, size, className }),
				"relative cursor-default whitespace-nowrap",
			)}
			{...props}
		>
			<Icon
				size={size === "lg" ? 18 : 14}
				className={variant === "PROCESSING" ? "animate-spin" : ""}
			/>
			<span>{displayText}</span>
		</div>
	);

	if (description === undefined) {
		return content;
	}

	return (
		<Tooltip>
			<TooltipTrigger asChild={true}>{content}</TooltipTrigger>
			<TooltipContent className="max-w-xs">{description}</TooltipContent>
		</Tooltip>
	);
}
